/**
 * Notification Toast Stack Component
 *
 * Displays a stack of persistent notification toasts.
 * Toasts are clickable (jump to item) and dismissible via X button.
 * Uses the centralized notification type registry for icons and colors.
 */

import React, { useState, useEffect } from 'react';
import type { NotificationToastItem } from '../store/notificationStore';
import { getNotificationConfig } from '../config/notificationTypes';
import { getLocationShortName } from '../store/locationsStore';

interface NotificationToastStackProps {
  toasts: NotificationToastItem[];
  onDismiss: (id: string) => void;
  onClick: (toast: NotificationToastItem) => void;
  onClearAll?: () => void; // Callback to clear all visible toasts
}

/**
 * Format timestamp as relative time (e.g., "2 min ago", "1 hr ago")
 */
const formatRelativeTime = (timestamp: number): string => {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHr < 24) return `${diffHr} hr ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
};

/**
 * Get user display string - prefer display name, fall back to email
 */
const getUserDisplay = (displayName?: string, email?: string): string => {
  if (displayName) return displayName;
  if (!email || email === 'system') return '';
  return email;
};

/**
 * Toast icon component using the centralized notification type registry
 */
const NotificationIcon = ({ type }: { type: string }) => {
  const config = getNotificationConfig(type);
  return (
    <div className="w-8 h-8 rounded-full bg-surface/20 flex items-center justify-center">
      <i className={`fa-solid ${config.icon} text-sm`}></i>
    </div>
  );
};

export const NotificationToastStack: React.FC<NotificationToastStackProps> = ({
  toasts,
  onDismiss,
  onClick,
  onClearAll,
}) => {
  // Force re-render every minute to update relative times
  const [, setTick] = useState(0);
  useEffect(() => {
    if (toasts.length === 0) return;
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, [toasts.length]);

  if (toasts.length === 0) return null;

  // Sort by timestamp ascending so oldest is at top, newest at bottom
  const sortedToasts = [...toasts].sort((a, b) => a.timestamp - b.timestamp);

  // Get color classes from registry
  const getToastColors = (type: string) => {
    const config = getNotificationConfig(type);
    return `${config.toastBg} ${config.toastHoverBg}`;
  };

  return (
    <div
      className="fixed bottom-4 right-4 z-[9998] hidden sm:flex flex-col gap-2 sm:w-96"
      role="region"
      aria-label="Notification toasts"
    >
      {/* Individual Toast Items */}
      {sortedToasts.map((toast, index) => {
        const userDisplay = getUserDisplay(toast.actionByName, toast.actionBy);
        const relativeTime = formatRelativeTime(toast.timestamp);
        const colorClasses = getToastColors(toast.type);

        return (
          <div
            key={toast.id}
            className={`${colorClasses} text-ink-inverse px-4 py-3 rounded-lg shadow-xl cursor-pointer transition-colors animate-slide-up`}
            style={{ animationDelay: `${index * 50}ms` }}
            onClick={() => onClick(toast)}
            role="alert"
            aria-live="polite"
          >
            <div className="flex gap-3 items-start">
              {/* Icon */}
              <div className="flex-shrink-0 mt-0.5">
                <NotificationIcon type={toast.type} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <p className="font-bold text-sm truncate">{toast.title}</p>
                    {toast.metadata?.personal !== 'true' && (
                      <span className="text-xs bg-surface/20 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                        {getLocationShortName(toast.locationId)}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-ink-inverse/70 flex-shrink-0">{relativeTime}</span>
                </div>
                <p className="text-sm text-ink-inverse/90 mt-1">{toast.body}</p>
                {userDisplay && (
                  <p className="text-xs text-ink-inverse/70 mt-1">by {userDisplay}</p>
                )}
              </div>

              {/* Close button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDismiss(toast.id);
                }}
                className="flex-shrink-0 text-ink-inverse/80 hover:text-ink-inverse transition-colors p-1 -mr-1 -mt-1"
                aria-label="Dismiss notification"
              >
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
          </div>
        );
      })}

      {/* Clear All Button - only show if there are toasts and callback is provided */}
      {sortedToasts.length > 0 && onClearAll && (
        <button
          onClick={onClearAll}
          className="bg-chrome hover:bg-chrome-deep text-ink-chrome px-4 py-2 rounded-lg shadow-xl transition-colors text-sm font-medium"
          aria-label="Clear all notifications"
        >
          <i className="fa-solid fa-trash-can mr-2"></i>
          Clear All ({sortedToasts.length})
        </button>
      )}
    </div>
  );
};
